/**
 * Layout-config publish store.
 *
 * A module-level `Map<componentId, PublishedLayoutConfig>` that captures the
 * **base** (pre-override) declarative config a configurable content component
 * renders from. It mirrors {@link runtimePropsStore} exactly: React-free,
 * in-memory, per-class last-write-wins, never serialised.
 *
 * ## Why a second store next to {@link runtimePropsStore}?
 *
 * The admin layout editor at `/admin/components/:componentId/config` builds its
 * editable target list (fields / columns / sections / title / actions) from
 * what the component declares. Today the editor scrapes the rendered preview
 * DOM (`discoverLayoutTargets`), which cannot reliably see:
 *
 * - a component **title** (hardcoded JSX, no marker),
 * - an action-button **label** (bespoke `<Button>`, no marker),
 * - a field's **placeholder** separately from its label (the DOM merges them),
 * - **hidden** fields/columns (they render no marker, so they vanish),
 * - **info-card items** (`InfoList` emits no per-item marker).
 *
 * Publishing the base config from the component — at the same site that calls
 * `applyLayoutOverrides` — gives the editor a reliable, complete model. DOM
 * scrape remains the fallback for not-yet-migrated components.
 *
 * Characteristics (identical philosophy to {@link runtimePropsStore}):
 *
 * - **Per-class, last-write-wins.** Multiple instances of the same component
 *   share one slot; the last publish wins. The published config is the *base*
 *   config (label/placeholder/order/visible as declared), so per-instance
 *   variation is irrelevant to the editor.
 *
 * - **Base, not effective.** Components publish the config **before**
 *   `applyLayoutOverrides`, so the editor shows true defaults and computes the
 *   *effective* values itself by overlaying the draft effects — exactly how it
 *   already overlays draft renames on `baseLabel`.
 *
 * - **Live references, not serialized.** The config object is kept by
 *   reference. A hard refresh resets the store to empty and the editor falls
 *   back to DOM discovery.
 *
 * - **In-memory only.** Never touches `localStorage`.
 *
 * - **No React imports.** Writes happen from {@link usePublishLayoutConfig} (a
 *   thin `useEffect` wrapper); reads happen from the config page. Keeping this
 *   file React-free makes it trivially reusable from unit tests without a
 *   render tree. Like {@link runtimePropsStore} it exposes a plain getter /
 *   setter / clear — no subscribe mechanism — so the editor re-reads it on the
 *   same trigger that re-scans the preview DOM.
 *
 * See `.claude-work/plans/layout-editor-free-text-editing.md` §2.1.
 */

import type {
  ActionsConfig,
  FieldConfig,
  SectionConfig,
} from '@components/integrated/entity-form';
import type {
  ColumnConfig,
  FilterConfig,
} from '@components/integrated/data-list';
import type { InfoItemDescriptor } from './layoutOverrides';

// ---------------------------------------------------------------------------
// Published shape
// ---------------------------------------------------------------------------

/**
 * Which declarative host a published config describes. Drives how
 * `discoverConfigTargets` reads it into {@link LayoutTarget}s.
 */
export type PublishedHostKind = 'entity-form' | 'data-list' | 'info-list';

/**
 * The base (pre-override) declarative config a configurable component
 * publishes for the editor to read.
 *
 * Only the buckets relevant to the {@link hostKind} are populated:
 *
 * - `'entity-form'` ⇒ `fields` (always), optional `sections` / `actions`.
 * - `'data-list'`   ⇒ `columns` (always), optional `filters` / `search`
 *                     (the toolbar inputs); no actions.
 * - `'info-list'`   ⇒ `items` (always); no placeholder/helper/actions.
 *
 * `title` is the **resolved base title** the card renders (EntityForm
 * `i18n.title` / DataList `i18n.title` ?? `display.title` / the card's own
 * title slot), if any — a single author-facing value regardless of host.
 */
export interface PublishedLayoutConfig {
  /** Which host this config describes. */
  hostKind: PublishedHostKind;
  /** EntityForm fields, keyed by field name. Base config (pre-override). */
  fields?: Record<string, FieldConfig>;
  /** EntityForm sections, in declaration order. */
  sections?: SectionConfig[];
  /** EntityForm action buttons (submit / cancel / reset / custom). */
  actions?: ActionsConfig;
  /** DataList columns, keyed by column key. Base config (pre-override). */
  columns?: Record<string, ColumnConfig>;
  /**
   * DataList toolbar filter inputs, in declaration order. Base config
   * (pre-override) so the editor lists every filter — including baseline-hidden
   * ones (which render no DOM marker) — with its stable `id`, `label`,
   * `placeholder`, `hidden`, and `order`.
   */
  filters?: FilterConfig[];
  /**
   * DataList toolbar **search** input (the single search box), if the host
   * configures one. Carries the editor-relevant `enabled` (visibility) and
   * `placeholder`. `undefined` ⇒ no search box.
   */
  search?: { enabled?: boolean; placeholder?: string };
  /** InfoList items, in declaration order. */
  items?: InfoItemDescriptor[];
  /**
   * Resolved base title (i18n.title ?? display.title ?? card title slot), if
   * the component renders one. `undefined` ⇒ no title.
   */
  title?: string;
}

// ---------------------------------------------------------------------------
// Internal storage
// ---------------------------------------------------------------------------

const layoutConfigStore = new Map<string, PublishedLayoutConfig>();

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Record the latest base layout config seen for a component id.
 *
 * {@link usePublishLayoutConfig} calls this from a `useEffect` whenever the
 * config's stable signature changes — it is a cheap store write with no
 * equality check of its own (the hook guards thrash). The config object is
 * kept by reference.
 *
 * @param id  - The component id (matches `ComponentRegistration.id`).
 * @param cfg - The base (pre-override) {@link PublishedLayoutConfig}.
 */
export function setLayoutConfig(id: string, cfg: PublishedLayoutConfig): void {
  layoutConfigStore.set(id, cfg);
}

/**
 * Look up the most recently published base config for a component id.
 *
 * Returns `undefined` when no component of that id has published in the
 * current session, or after a hard refresh — the config page uses this signal
 * to fall back to DOM-scraped layout targets.
 */
export function getLayoutConfig(id: string): PublishedLayoutConfig | undefined {
  return layoutConfigStore.get(id);
}

/**
 * Clear published layout configs.
 *
 * - `clearLayoutConfig()` — clears every entry (test-only / teardown).
 * - `clearLayoutConfig("<id>")` — clears a single slot. Used by tests that
 *   want to isolate a single entry.
 */
export function clearLayoutConfig(id?: string): void {
  if (id === undefined) {
    layoutConfigStore.clear();
    return;
  }
  layoutConfigStore.delete(id);
}
